import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import SiteHeader from "@/components/landing/SiteHeader";
import SiteFooterCompact from "@/components/landing/SiteFooterCompact";
import { TOPICS, GROUPS } from "@/pages/knowledge/topics";
import {
  PayoffCall,
  PayoffPut,
  OptionChainViz,
  VIXMeter,
  Sparkline,
  GreeksGrid,
} from "@/pages/knowledge/Visuals";
import { ActiveTopicContext } from "@/pages/knowledge/Atoms";
import { ChevronRight, ChevronLeft, ChevronsRight } from "lucide-react";
import KH_BASICS from "@/pages/knowledge/sections/Basics";
import KH_OPTIONS from "@/pages/knowledge/sections/Options";
import KH_TOOLS from "@/pages/knowledge/sections/Tools";
import KH_INDICES from "@/pages/knowledge/sections/Indices";
import KH_ADVANCED from "@/pages/knowledge/sections/Advanced";
import KH_STRATEGIES from "@/pages/knowledge/sections/Strategies";

const ALL_SECTIONS = {
  Basics: KH_BASICS,
  Options: KH_OPTIONS,
  Tools: KH_TOOLS,
  Indices: KH_INDICES,
  Advanced: KH_ADVANCED,
  Strategies: KH_STRATEGIES,
};

const VISUALS = { PayoffCall, PayoffPut, OptionChainViz, VIXMeter, Sparkline, GreeksGrid };

export default function KnowledgeHub() {
  const [active, setActive] = useState(TOPICS[0].id);

  useEffect(() => {
    document.title = "Knowledge Hub — Tradecure | Learn options, F&O, algo trading";
    const meta = document.querySelector('meta[name="description"]');
    const desc =
      "Free, jargon-free learning resources on options, F&O, algo trading, hedging, risk management and Indian indices. Curated by Tradecure for the 90% retail trader.";
    if (meta) meta.setAttribute("content", desc);
    else {
      const m = document.createElement("meta");
      m.name = "description";
      m.content = desc;
      document.head.appendChild(m);
    }
  }, []);

  useEffect(() => {
    // On topic change, scroll the content column to top
    const el = document.getElementById("kh-content-top");
    if (el) {
      const y = el.getBoundingClientRect().top + window.scrollY - 80;
      window.scrollTo({ top: y, behavior: "smooth" });
    }
  }, [active]);

  const idx = TOPICS.findIndex((t) => t.id === active);
  const prev = idx > 0 ? TOPICS[idx - 1] : null;
  const next = idx < TOPICS.length - 1 ? TOPICS[idx + 1] : null;
  const activeTopic = TOPICS[idx];

  return (
    <main data-testid="knowledge-hub-page" className="bg-white text-[#0f172a]">
      <SiteHeader />

      {/* Hero */}
      <section className="relative pt-16 pb-12 md:pt-20 md:pb-14 border-b border-[#e2e8f0] overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-60 pointer-events-none" />
        <div className="relative mx-auto max-w-7xl px-5 md:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="font-mono text-xs font-bold text-[#084392] tracking-[0.22em]">
              / KNOWLEDGE HUB
            </div>
            <h1 className="font-heading mt-4 text-5xl md:text-6xl lg:text-7xl tracking-tighter leading-[0.98]">
              Learn the market.
              <br />
              <span className="text-[#084392]">In plain English.</span>
            </h1>
            <p className="mt-5 max-w-2xl text-lg text-[#475569]">
              Eighteen bite-sized chapters covering everything from options and
              F&amp;O to algo trading, Greeks, indices and intraday strategies —
              with Indian market examples and the latest lot sizes &amp;
              expiries.
            </p>
            <div className="mt-6 flex flex-wrap gap-2">
              {GROUPS.map((g) => (
                <span
                  key={g}
                  className="text-xs font-semibold font-mono tracking-[0.15em] px-3 py-1.5 rounded-full bg-white border border-[#e2e8f0] text-[#475569]"
                >
                  {g.toUpperCase()}
                </span>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Body: sticky TOC + single-topic content */}
      <section
        id="kh-content-top"
        className="mx-auto max-w-7xl px-5 md:px-8 py-12 grid lg:grid-cols-12 gap-10"
      >
        {/* TOC */}
        <aside className="lg:col-span-3" data-testid="kh-toc">
          <nav className="lg:sticky lg:top-24 lg:max-h-[calc(100vh-7rem)] lg:overflow-y-auto pr-2">
            <div className="font-mono text-xs font-bold text-[#0f172a] mb-3">
              {String(idx + 1).padStart(2, "0")} of{" "}
              {String(TOPICS.length).padStart(2, "0")}
            </div>
            {GROUPS.map((g) => (
              <div key={g} className="mb-6">
                <div className="font-mono text-[10px] font-bold text-[#084392] tracking-[0.22em] mb-2">
                  {g.toUpperCase()}
                </div>
                <ul className="space-y-1">
                  {TOPICS.filter((t) => t.group === g).map((t) => {
                    const n = TOPICS.findIndex((x) => x.id === t.id) + 1;
                    const isActive = active === t.id;
                    return (
                      <li key={t.id}>
                        <button
                          onClick={() => setActive(t.id)}
                          data-testid={`toc-${t.id}`}
                          className={`w-full text-left text-sm py-2 px-3 rounded-md transition flex items-center gap-2 ${
                            isActive
                              ? "bg-[#084392] text-white font-semibold"
                              : "text-[#475569] hover:bg-[#f8fafc] hover:text-[#0f172a]"
                          }`}
                        >
                          <span
                            className={`font-mono text-[10px] shrink-0 ${
                              isActive ? "text-white/80" : "text-[#94a3b8]"
                            }`}
                          >
                            {String(n).padStart(2, "0")}
                          </span>
                          <span className="flex-1">{t.title}</span>
                          {isActive && <ChevronRight size={14} />}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))}
          </nav>
        </aside>

        {/* Content — only active topic renders */}
        <div className="lg:col-span-9">
          <ActiveTopicContext.Provider value={active}>
            <motion.div
              key={active}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.35 }}
            >
              {GROUPS.map((g) => (
                <React.Fragment key={g}>
                  {ALL_SECTIONS[g](VISUALS)}
                </React.Fragment>
              ))}
            </motion.div>
          </ActiveTopicContext.Provider>

          {/* Prev / Next navigation */}
          <div className="mt-12 grid sm:grid-cols-2 gap-3" data-testid="kh-prev-next">
            {prev ? (
              <button
                onClick={() => setActive(prev.id)}
                data-testid="kh-prev"
                className="text-left rounded-xl border border-[#e2e8f0] p-4 hover:border-[#084392]/40 hover:bg-[#f8fafc] transition group"
              >
                <div className="flex items-center gap-1.5 font-mono text-[10px] font-bold text-[#475569] tracking-[0.18em]">
                  <ChevronLeft size={12} /> PREVIOUS
                </div>
                <div className="mt-1 font-heading text-base font-bold text-[#0f172a] group-hover:text-[#084392]">
                  {prev.title}
                </div>
              </button>
            ) : (
              <div />
            )}
            {next ? (
              <button
                onClick={() => setActive(next.id)}
                data-testid="kh-next"
                className="text-right rounded-xl border border-[#e2e8f0] p-4 hover:border-[#084392]/40 hover:bg-[#f8fafc] transition group sm:col-start-2"
              >
                <div className="flex items-center justify-end gap-1.5 font-mono text-[10px] font-bold text-[#475569] tracking-[0.18em]">
                  NEXT <ChevronsRight size={12} />
                </div>
                <div className="mt-1 font-heading text-base font-bold text-[#0f172a] group-hover:text-[#084392]">
                  {next.title}
                </div>
              </button>
            ) : null}
          </div>

          {/* Closing CTA — only on last topic */}
          {!next && (
            <div className="mt-12 rounded-2xl border border-[#084392]/20 bg-[#084392] text-white p-8 md:p-10">
              <h3 className="font-heading text-3xl md:text-4xl tracking-tight">
                You&rsquo;ve covered the essentials.
              </h3>
              <p className="mt-3 text-white/80 max-w-xl">
                Theory&rsquo;s great — but the market doesn&rsquo;t pay you for
                knowing. Let Tradecure automate the discipline you&rsquo;ve just
                learned.
              </p>
              <a
                href="/#waitlist"
                data-testid="kh-cta-waitlist"
                className="mt-6 inline-flex items-center h-12 px-6 rounded-md bg-white text-[#084392] font-bold hover:bg-[#f8fafc] transition"
              >
                Join the waitlist
              </a>
            </div>
          )}
        </div>
      </section>

      <SiteFooterCompact />
    </main>
  );
}
